import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { clearAccessTokens, NumberFormat } from '@ecp/common/src/utils/utils';
import { removeCookie } from '@ecp/common/src/utils/cookieUtils';
import { useLogin } from '@ecp/common/src/hooks/useLoginForward';
import { theme } from '@ecp/common/src/style/theme/default';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { NoneDecorationLink, StyledTextLink } from '@ecp/common/src/text/TextLink';
import ContextMenu from '@ecp/common/src/components/contextmenu/ContextMenu';
import ContextMenuItem from '@ecp/common/src/components/contextmenu/ContextMenuItem';
import StyledImg from '@ecp/common/src/components/image/StyledImg';
import { TextButton } from '@ecp/common/src/components/button/bo/TextButton';
import { SiteNumberFunc, SITE_DS_BIRTH_POINT } from '@ecp/common/src/const/site/siteNumberConst.js'; // ECMJ
import { ReactComponent as IconCoupon } from '@ecp/common/src/assets/icon/icon__asset-coupon.svg';
import { ReactComponent as IconPoint } from '@ecp/common/src/assets/icon/icon__asset-welpoint.svg';

import { withCartBadgeCount, withPoint, withCouponCount } from '@fo-recoil/order/selector';
import { myDeliveryPlaceListState } from '@fo-recoil/order/atom';
import { userInfoState } from '@fo-recoil/common/user/atom';
import { useConfirmDialog } from '@fo-hooks/common/useModal';
import useSiteInfo from '@fo-hooks/common/useSiteInfo';
import authApi from '@fo-apis/common/authApi';
import { MainContentsBox } from '@fo-components/common/styled';
import GnbIconButton from '@fo-pages/home/gnb/GnbIconButton';
import GnbIconButton2 from '@fo-pages/home/gnb/GnbIconButton2';
import BulkOrderPopupButton from '@fo-pages/home/footer/popup/bulkOrder/BulkOrderPopupButton';
import { ReactComponent as IconMyPage } from '@fo-assets/icon/icon__gnb-mypage.svg';
import { ReactComponent as IconOrder } from '@fo-assets/icon/icon__gnb-order.svg';
import { ReactComponent as IconCart } from '@fo-assets/icon/icon__gnb-cart.svg';
import { ReactComponent as IconCustomerService } from '@fo-assets/icon/icon__gnb-cs.svg';
import { useSiteComponentResolver } from '@ecp/fo/src/hooks/resolver/SiteComponentResolverHook';

const GNB_LINK_URLS = {
  MY_PAGE: '/my-page',
  MEMBER: '/member',
  ORDER: '/my-page/order',
  CART: 'cart',
  COUPON: '/my-page/coupon',
  POINT: '/my-page/asset-point',
  CUSTOMER_SERVICE: '/customer-service',
  CUSTOMER_SERVICE_NOTICE: '/customer-service/notice',
};

const GnbHeader = React.forwardRef((searchPhraseList, ref) => {
  const { showConfirmDialog } = useConfirmDialog();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    isMultimall,
    isCartUse,
    isCouponMenuUse,
    isPointUse,
    displayPointName,
    systemInformation: { pcGnbImgFilePath },
    isNoMemberUseYn,
    siteNumber,
    isEventMall,
  } = useSiteInfo();

  const { login } = useRecoilValue(userInfoState);
  const cartBadgeCount = useRecoilValue(withCartBadgeCount);
  const point = useRecoilValue(withPoint);
  const couponCount = useRecoilValue(withCouponCount);
  const resetDeliveryPlaceList = useResetRecoilState(myDeliveryPlaceListState);
  const loginForward = useLogin(siteNumber);

  const logout = useCallback(async () => {
    await authApi.logout();
    clearAccessTokens();
    // PRG_EXON_02_01 (옥진호) : 로그아웃시 쿠키 정보를 삭제 처리함.
    if (SiteNumberFunc.isCookieGateUseYn(siteNumber)) {
      removeCookie(SiteNumberFunc.cookieGateName(siteNumber), { path: '/' });
    }
    resetDeliveryPlaceList();
    navigate(isNoMemberUseYn ? '/' : '/logout');
  }, [isNoMemberUseYn, navigate, resetDeliveryPlaceList, siteNumber]);

  const handleLogoutClick = useCallback(() => {
    showConfirmDialog('로그아웃 하시겠습니까?', logout);
  }, [logout, showConfirmDialog]);

  const handleLoginClick = () => {
    if (!login) {
      loginForward();
    }
  };

  const { isMyPage, isOrder, isCart, isCustomerService } = useMemo(
    () => ({
      isMyPage:
        pathname.endsWith(GNB_LINK_URLS.MEMBER) ||
        (!pathname.includes(GNB_LINK_URLS.ORDER) && pathname.includes(GNB_LINK_URLS.MY_PAGE)),
      isOrder: pathname.includes(GNB_LINK_URLS.ORDER),
      isCart: pathname.includes(GNB_LINK_URLS.CART),
      isCustomerService: pathname.includes(GNB_LINK_URLS.CUSTOMER_SERVICE),
    }),
    [pathname]
  );

  const isCustomerServiceMenuUseYn = useMemo(() => {
    return SiteNumberFunc.isCustomerServiceMenuUseYn(siteNumber);
  }, [siteNumber]);

  // ECMJ
  const ResolvedCustomerCenter = useSiteComponentResolver(
    <GnbIconButton
      iconType={IconCustomerService}
      label={'고객센터'}
      selected={isCustomerService}
      to={GNB_LINK_URLS.CUSTOMER_SERVICE}
      data-ds-label2={'g_cs'}
    />,
    {
      [SITE_DS_BIRTH_POINT]: (
        <GnbIconButton
          iconType={IconCustomerService}
          label={'고객센터'}
          selected={isCustomerService}
          to={GNB_LINK_URLS.CUSTOMER_SERVICE_NOTICE}
          data-ds-label2={'g_cs'}
        />
      ),
    },
    isEventMall,
    <GnbIconButton
      iconType={IconCustomerService}
      label={'고객센터'}
      selected={isCustomerService}
      to={GNB_LINK_URLS.CUSTOMER_SERVICE}
      data-ds-label2={'g_cs'}
    />
  );

  return (
    <Wrapper ref={ref}>
      <FlexBox>
        <NoneDecorationLink to={''}>
          <Logo data-ds-label2={'g_logo'} src={pcGnbImgFilePath} />
        </NoneDecorationLink>
      </FlexBox>
      <FlexBox gap={'15px'}>
        {isNoMemberUseYn && !login && <LoginText onClick={handleLoginClick}>로그인</LoginText>}
        {isCouponMenuUse && (
          <GnbIconButton2
            iconType={IconCoupon}
            label={'쿠폰'}
            to={GNB_LINK_URLS.COUPON}
            iconCount={couponCount ? NumberFormat(couponCount) : 0}
            data-ds-label2={'g_coupon'}
            unit={'장'}
          />
        )}
        {isPointUse && (
          <GnbIconButton2
            iconType={IconPoint}
            label={displayPointName}
            iconCount={point ? NumberFormat(point.assetBalance):0}
            to={GNB_LINK_URLS.POINT}
            data-ds-label2={'g_point'}
            unit={'P'}
          />
        )}
        {login && (
          <ContextMenu
            openWhenHovered
            placement={'bottom-end'}
            value={<MyPageMenuItems clickWhenLogout={handleLogoutClick} />}
          >
            <GnbIconButton
              iconType={IconMyPage}
              label={'마이페이지'}
              selected={isMyPage}
              to={GNB_LINK_URLS.MY_PAGE}
              data-ds-label2={'g_my_page'}
            />
          </ContextMenu>
        )}
        {!login && (
          <GnbIconButton
            iconType={IconMyPage}
            label={'마이페이지'}
            selected={isMyPage}
            to={GNB_LINK_URLS.MY_PAGE}
            data-ds-label2={'g_my_page'}
          />
        )}
        {login && (
          <GnbIconButton
            iconType={IconOrder}
            label={'주문내역'}
            selected={isOrder}
            to={GNB_LINK_URLS.ORDER}
            data-ds-label2={'g_order_history'}
          />
        )}
        {isCartUse && (
          <GnbIconButton
            showZero
            iconType={IconCart}
            badgeContent={cartBadgeCount}
            label={'장바구니'}
            selected={isCart}
            to={GNB_LINK_URLS.CART}
            data-ds-label2={'g_basket'}
          />
        )}
        {isCustomerServiceMenuUseYn && ResolvedCustomerCenter}
        {!isMultimall && <BulkOrderPopupButton isIcon={true} />}
      </FlexBox>
    </Wrapper>
  );
});

GnbHeader.displayName = 'GnbHeader';
export default GnbHeader;

const MyPageMenuItems = (props) => {
  const { clickWhenLogout: handleLogoutClick } = props;
  const { siteNumber } = useSiteInfo();

  const myPageEditMenuUseYn = useMemo(() => {
    return SiteNumberFunc.myPageEditMenuUseYn(siteNumber);
  }, [siteNumber]);

  return (
    <FlexBoxColumn gap="6px" width="100%" align-items="flex-start">
      <ContextMenuItem size={'13px'} to="/my-page">
        마이페이지
      </ContextMenuItem>
      {myPageEditMenuUseYn && (
        <ContextMenuItem size={'13px'} to="/my-page/edit">
          내정보수정
        </ContextMenuItem>
      )}
      <StyledTextLink size={'13px'} decorationOnHover onClick={handleLogoutClick}>
        로그아웃
      </StyledTextLink>
    </FlexBoxColumn>
  );
};

MyPageMenuItems.propTypes = {
  clickWhenLogout: PropTypes.func,
};

const Wrapper = styled(MainContentsBox)`
  height: 50px;
  justify-content: space-between;
  min-width: 950px;
`;

const Logo = styled(StyledImg)`
  margin-right: ${(props) => props.theme.component.gnb.logo.marginRight};
  width: 145px;
  height: 30px;
`;

const LoginText = styled(TextButton)`
  font-size: 12px;
  line-height: 2px;
  font-weight: ${theme.font.weight.demiLight};
`;
